import type { BandName, MeterConfig, PersonaProfile, Tier } from '../shared/types';

/**
 * The sentiment meter (§10.4).
 *
 * Internal 0–100, displayed as five bands and never as a number. Movement runs
 * through the persona's multipliers for the whole session, which is what makes
 * the same option land differently on a burned prospect and a dreamer.
 */

/**
 * Which band a value sits in.
 *
 * Bands are authored as inclusive integer ranges (`neutral: [40, 59]`,
 * `warm: [60, 79]`) but sentiment is fractional, so 59.6 belongs to no range as
 * written. Selecting by lower bound closes those gaps and keeps the mapping
 * monotonic: a value can only ever move up a band by getting larger.
 *
 * Matching the ranges literally used to leave fractional values between bands,
 * and the fallthrough resolved them as `hot` — so a call sitting just under
 * Warm displayed as Hot, which is the worst direction for this particular lie.
 */
export function bandFor(value: number, bands: MeterConfig['bands']): BandName {
  const ascending = (Object.entries(bands) as [BandName, [number, number]][])
    .sort((a, b) => a[1][0] - b[1][0]);

  let chosen = ascending[0]![0];
  for (const [name, [lower]] of ascending) {
    if (value >= lower) chosen = name;
  }
  return chosen;
}

export const clamp = (n: number) => Math.max(0, Math.min(100, n));

/**
 * §4.2 — a setter opens cold because they interrupted a stranger, adjusted by
 * the persona's own starting offset. A closer inherits the band the setter
 * earned, declared per scenario.
 */
export function startingSentiment(
  role: string,
  persona: PersonaProfile,
  meter: MeterConfig,
  inheritedBand?: BandName
): number {
  if (inheritedBand) {
    const range = meter.bands[inheritedBand];
    const midpoint = Math.round((range[0] + range[1]) / 2);
    return clamp(midpoint + persona.sentiment.startOffset);
  }
  return clamp((meter.startByRole[role] ?? 50) + persona.sentiment.startOffset);
}

export interface Movement {
  from: number;
  to: number;
  applied: number;
  tier: Tier;
  bonusApplied: boolean;
  /** The move was the right play for the wrong person (§5.3.4). */
  mismatched: boolean;
}

/**
 * §5.3.4 — a rep who misreads the prospect is served the option set for the
 * persona they named, and those moves "land badly". They are good moves; they
 * are simply aimed at somebody else.
 *
 * The specification requires the behavior without naming a mechanism, so this
 * is the mechanism: on a persona-sensitive node whose served set was authored
 * for a different persona, a gain is reduced and a loss is deepened, scaled by
 * how wrong the read was (§5.6). The scale comes from the content pack's
 * personaSeverityPenalty, so the engine holds no number of its own — a near
 * miss costs a quarter of the move, and reading someone as their opposite
 * costs all of it.
 */
export function mismatchAdjust(delta: number, penalty: number): number {
  return delta > 0 ? delta * Math.max(0, 1 - penalty) : delta * (1 + penalty);
}

/**
 * One meter movement:
 *
 *   delta × (delta < 0 ? negativeMultiplier : 1) × persona.tierMultipliers[tier]
 *         × (categoryBonus if applicable)
 *
 * The persona multiplier is applied *after* the global negativeMultiplier, per
 * §5.2 — a burned prospect punishes mistakes harder and warms slower, and the
 * two multipliers compound rather than replace one another.
 */
export function applyDelta(
  current: number,
  args: {
    delta: number;
    tier: Tier;
    persona: PersonaProfile;
    meter: MeterConfig;
    /** The category this node actually tests, where the node tests one. */
    category?: string;
    /** A compliance trap overrides the option's own delta (§10.4). */
    compliance?: boolean;
    /** Penalty for serving a set authored for a persona this is not (§5.3.4). */
    mismatchPenalty?: number;
  }
): Movement {
  const { delta, tier, persona, meter, category, compliance, mismatchPenalty } = args;

  if (compliance) {
    const to = clamp(current + meter.complianceViolationDelta);
    return { from: current, to, applied: to - current, tier, bonusApplied: false, mismatched: false };
  }

  const direction = delta < 0 ? meter.negativeMultiplier : 1;
  const personaMultiplier = persona.sentiment.tierMultipliers[tier] ?? 1;

  // §5.2 — the bonus recognizes handling the category this persona is most
  // sensitive about, so it only applies when the handling was actually good.
  const bonus = category ? persona.sentiment.categoryBonus?.[category] : undefined;
  const bonusApplies = bonus !== undefined && (tier === 'best' || tier === 'good');

  const mismatched = !!mismatchPenalty;
  const effective = mismatched ? mismatchAdjust(delta, mismatchPenalty!) : delta;

  const applied = effective * direction * personaMultiplier * (bonusApplies ? bonus : 1);
  const to = clamp(current + applied);
  return { from: current, to, applied: to - current, tier, bonusApplied: bonusApplies, mismatched };
}
